/**
 * Shared styles and theme colors for activity detail components.
 * These are extracted from the parent ActivityDetailView to ensure
 * consistent styling across all detail renderers.
 */
/**
 * Theme colors used throughout the activity detail views.
 * Dark theme aligned with the Turn Connect design system.
 */
export const colors = {
    background: {
        primary: '#111827',
        secondary: '#1F2937',
        tertiary: '#374151',
    },
    text: {
        primary: '#FFFFFF',
        secondary: '#9CA3AF',
        tertiary: '#6B7280',
    },
    border: {
        default: '#374151',
    },
    accent: {
        blue: '#3B82F6',
        green: '#22C55E',
        red: '#EF4444',
        amber: '#F59E0B',
        purple: '#8B5CF6',
        cyan: '#06B6D4',
    },
};
/**
 * Shared styles used across all detail components.
 * Uses CSS-in-JS inline styles for cross-platform compatibility.
 */
export const detailStyles = {
    detailContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
    },
    sectionHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '13px',
        fontWeight: 600,
        color: colors.text.primary,
        marginBottom: '8px',
    },
    infoRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '8px 0',
        borderBottom: `1px solid ${colors.border.default}`,
    },
    infoLabel: {
        fontSize: '12px',
        color: colors.text.tertiary,
        minWidth: '100px',
    },
    infoValue: {
        fontSize: '12px',
        color: colors.text.primary,
        flex: 1,
    },
    linkText: {
        color: colors.accent.blue,
        cursor: 'pointer',
        textDecoration: 'underline',
    },
    messageBox: {
        backgroundColor: colors.background.secondary,
        borderRadius: '6px',
        padding: '12px',
        fontSize: '13px',
        color: colors.text.secondary,
        lineHeight: 1.5,
        fontStyle: 'italic',
        borderLeft: `3px solid ${colors.accent.amber}`,
    },
    stateTransition: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '16px',
        padding: '16px',
        backgroundColor: colors.background.secondary,
        borderRadius: '6px',
        flexWrap: 'wrap',
    },
    stateBox: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '4px',
        padding: '12px 24px',
        backgroundColor: colors.background.tertiary,
        borderRadius: '6px',
        minWidth: '100px',
    },
    stateLabel: {
        fontSize: '11px',
        color: colors.text.tertiary,
        textTransform: 'uppercase',
    },
    stateValue: {
        fontSize: '13px',
        fontWeight: 600,
        color: colors.text.primary,
    },
    emptyState: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        padding: '24px',
        color: colors.text.tertiary,
        fontSize: '13px',
    },
    recipientList: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        backgroundColor: colors.background.secondary,
        borderRadius: '6px',
        padding: '12px',
    },
    recipientItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '8px',
        backgroundColor: colors.background.tertiary,
        borderRadius: '4px',
    },
    recipientName: {
        flex: 1,
        fontSize: '13px',
        color: colors.text.primary,
    },
    recipientType: {
        fontSize: '11px',
        color: colors.text.tertiary,
        textTransform: 'uppercase',
    },
    messageSection: {
        marginTop: '8px',
    },
    summaryRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '8px 12px',
        backgroundColor: colors.background.secondary,
        borderRadius: '4px',
        fontSize: '12px',
        color: colors.text.secondary,
    },
    summaryText: {
        color: colors.text.secondary,
    },
    reasonSection: {
        display: 'flex',
        gap: '8px',
        padding: '8px 12px',
        backgroundColor: colors.background.secondary,
        borderRadius: '4px',
    },
    reasonLabel: {
        fontSize: '12px',
        color: colors.text.tertiary,
    },
    reasonValue: {
        fontSize: '12px',
        color: colors.text.primary,
    },
    noteFlags: {
        display: 'flex',
        gap: '16px',
        padding: '8px 0',
        flexWrap: 'wrap',
    },
    flagItem: {
        display: 'flex',
        gap: '8px',
        fontSize: '12px',
    },
    flagLabel: {
        color: colors.text.tertiary,
    },
    changesSection: {
        marginTop: '8px',
    },
    changesTable: {
        backgroundColor: colors.background.secondary,
        borderRadius: '6px',
        overflow: 'hidden',
    },
    changesHeader: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        gap: '8px',
        padding: '8px 12px',
        backgroundColor: colors.background.tertiary,
        borderBottom: `1px solid ${colors.border.default}`,
    },
    changesHeaderCell: {
        fontSize: '11px',
        fontWeight: 600,
        color: colors.text.tertiary,
        textTransform: 'uppercase',
    },
    changesRow: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        gap: '8px',
        padding: '8px 12px',
        borderBottom: `1px solid ${colors.border.default}`,
    },
    changesFieldCell: {
        fontSize: '12px',
        color: colors.text.primary,
    },
    changesValueCell: {
        fontSize: '12px',
        wordBreak: 'break-word',
    },
    changeCards: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
        gap: '12px',
    },
    changeCard: {
        backgroundColor: colors.background.secondary,
        borderRadius: '8px',
        padding: '12px',
        border: `1px solid ${colors.border.default}`,
    },
    changeCardHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '8px',
    },
    changeCardLabel: {
        fontSize: '12px',
        fontWeight: 600,
        color: colors.text.secondary,
        textTransform: 'uppercase',
    },
    changeCardValue: {
        fontSize: '13px',
        color: colors.text.primary,
        wordBreak: 'break-word',
    },
    changeCardOldNew: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        flexWrap: 'wrap',
    },
    changeCardOldValue: {
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
        flex: 1,
        minWidth: '60px',
    },
    changeCardNewValue: {
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
        flex: 1,
        minWidth: '60px',
    },
    changeCardOldLabel: {
        fontSize: '10px',
        color: colors.text.tertiary,
        textTransform: 'uppercase',
    },
    changeCardOldText: {
        fontSize: '12px',
        color: colors.accent.red,
        wordBreak: 'break-word',
        textDecoration: 'line-through',
        opacity: 0.8,
    },
    changeCardNewLabel: {
        fontSize: '10px',
        color: colors.text.tertiary,
        textTransform: 'uppercase',
    },
    changeCardNewText: {
        fontSize: '12px',
        color: colors.accent.green,
        wordBreak: 'break-word',
        fontWeight: 500,
    },
    changesSummary: {
        marginTop: '12px',
        padding: '8px 12px',
        backgroundColor: colors.background.tertiary,
        borderRadius: '4px',
        textAlign: 'center',
    },
    changesSummaryText: {
        fontSize: '12px',
        color: colors.text.secondary,
    },
    additionalInfo: {},
};
